/**
 * Zentrale Raum-Konfiguration
 * Die einzige Wahrheitsquelle für Raumtypen in der gesamten Anwendung.
 */

export type RoomCategory = 'indoor' | 'outdoor';

export interface RoomConfig {
  id: string;
  label: string; // Deutscher Name
  category: RoomCategory;
  supportsStaging: boolean;
}

/**
 * ROOMS Dictionary
 * Enthält alle unterstützten Raumtypen mit deutschen Labels und Staging-Support
 */
export const ROOMS: Record<string, RoomConfig> = {
  // ============ Innenbereich ============
  living_room: { 
    id: 'living_room', 
    label: 'Wohnzimmer', 
    category: 'indoor', 
    supportsStaging: true 
  },
  bedroom: { 
    id: 'bedroom', 
    label: 'Schlafzimmer', 
    category: 'indoor', 
    supportsStaging: true 
  },
  kitchen: { 
    id: 'kitchen', 
    label: 'Küche', 
    category: 'indoor', 
    supportsStaging: true 
  },
  bathroom: { 
    id: 'bathroom', 
    label: 'Badezimmer', 
    category: 'indoor', 
    supportsStaging: true 
  },
  dining: { 
    id: 'dining', 
    label: 'Esszimmer', 
    category: 'indoor', 
    supportsStaging: true 
  },
  office: { 
    id: 'office', 
    label: 'Arbeitszimmer', 
    category: 'indoor', 
    supportsStaging: true 
  },
  hallway: { 
    id: 'hallway', 
    label: 'Flur / Diele', 
    category: 'indoor', 
    supportsStaging: true 
  },
  staircase: { 
    id: 'staircase', 
    label: 'Treppenhaus', 
    category: 'indoor', 
    supportsStaging: false // Meist nur cleanen
  },
  walk_in_closet: { 
    id: 'walk_in_closet', 
    label: 'Ankleidezimmer', 
    category: 'indoor', 
    supportsStaging: true 
  },

  // ============ Außenbereich ============
  facade: { 
    id: 'facade', 
    label: 'Fassade / Hausansicht', 
    category: 'outdoor', 
    supportsStaging: false 
  },
  garden: { 
    id: 'garden', 
    label: 'Garten', 
    category: 'outdoor', 
    supportsStaging: false 
  },
  balcony: { 
    id: 'balcony', 
    label: 'Balkon', 
    category: 'outdoor', 
    supportsStaging: true // Balkon kann man stagen!
  },
  terrace: { 
    id: 'terrace', 
    label: 'Terrasse', 
    category: 'outdoor', 
    supportsStaging: true 
  },
  entrance_outdoor: { 
    id: 'entrance_outdoor', 
    label: 'Eingangsbereich (Außen)', 
    category: 'outdoor', 
    supportsStaging: false 
  },

  // ============ Sonstiges ============
  custom: { 
    id: 'custom', 
    label: 'Sonstiges', 
    category: 'indoor', 
    supportsStaging: true 
  },
};

/**
 * Helper: Get all indoor rooms
 */
export const getIndoorRooms = (): RoomConfig[] => 
  Object.values(ROOMS).filter(room => room.category === 'indoor');

/**
 * Helper: Get all outdoor rooms
 */
export const getOutdoorRooms = (): RoomConfig[] => 
  Object.values(ROOMS).filter(room => room.category === 'outdoor');

/**
 * Helper: Get room config by ID (with fallback to custom)
 */
export const getRoomConfig = (roomId: string): RoomConfig => 
  ROOMS[roomId] || ROOMS.custom;

/**
 * Helper: Check if staging is supported for a room type
 */
export const supportsStaging = (roomId: string): boolean => 
  getRoomConfig(roomId).supportsStaging;

/**
 * Helper: Get german label for a room type
 */
export const getRoomLabel = (roomId: string): string => 
  getRoomConfig(roomId).label;

/**
 * Helper: Get room category
 */
export const getRoomCategory = (roomId: string): RoomCategory =>
  getRoomConfig(roomId).category;

// ============================================================================
// Staging Styles
// ============================================================================

export const STAGING_STYLES = [
  { id: 'modern' as const, label: 'Modern', description: 'Skandinavisch & Minimalistisch', icon: 'Home' },
  { id: 'luxury' as const, label: 'Luxuriös', description: 'Elegant & High-End', icon: 'Crown' },
  { id: 'industrial' as const, label: 'Industrial', description: 'Urban Loft Style', icon: 'Factory' },
] as const;

